import { useTranslation } from 'react-i18next';
import type { LobbyTableInfo } from '@draughts/shared';
import { isDrawnOpening, DEFAULT_GAME_MODE, canJoinMatchType } from '@draughts/shared';
import { UserAvatar } from '../game/UserAvatar';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { CADENCE_ICONS, parseTimeControl } from '../../utils/cadence-icons';
import { gameModeLabel, matchTypeLabel } from '../../utils/challenge-labels';
import { openingOptionLabel } from '../../utils/opening-labels';

interface Props {
  table: LobbyTableInfo;
  /** True for our own table: the action becomes "cancel" and there is no sitting. */
  isMine: boolean;
  /** A guest cannot take a seat at a "solo valida" table — the server refuses it. */
  isGuest: boolean;
  /** Disables every action while a request is in flight. */
  busy: boolean;
  onSit: (tableId: string) => void;
  onCancel: () => void;
}

/**
 * One open table in the list.
 *
 * The game-type pill is what makes a mixed pool honest: a registered player
 * clicking a guest's table can see beforehand that the game will not count,
 * instead of finding out from the board header afterwards.
 *
 * A "solo valida" table stays in the list for a guest, with the seat disabled
 * and the reason next to it. Hiding it would be tidier and would also hide the
 * one thing that might make them register.
 *
 * Your OWN table is this same row, pinned at the top by the list and carrying
 * the spinner and "waiting for an opponent". It used to be a second card above
 * the list saying the same things with the same Cancel button, while the row
 * itself sat at the bottom of the queue — the table drawn twice, and neither
 * copy where you would look for it.
 */
export default function LobbyTableRow({ table, isMine, isGuest, busy, onSit, onCancel }: Props) {
  const { t } = useTranslation();
  const cadence = parseTimeControl(table.timeControl);
  const drawn = isDrawnOpening(table.opening);
  // A table from a client on a cached bundle carries no matchType; 'any' is what
  // its `rated` boolean has always meant.
  const matchType = table.matchType ?? (table.rated ? 'any' : 'casual');
  const barred = !isMine && !canJoinMatchType(matchType, { isGuest });

  return (
    <li className={`lobby-row${isMine ? ' is-mine' : ''}`}>
      <div className="lobby-row-host">
        <UserAvatar username={table.hostUsername} avatar={table.hostAvatar} variant="opponent" size="sm" />
        <div className="lobby-row-identity">
          <span className="lobby-row-name">{table.hostUsername}</span>
          {/* Guests and bots have no rating, and the field is null for them.
              Rendering it unconditionally would print a bare `0` — Math.round(null)
              — which reads as a real, very bad rating rather than as "none". */}
          {table.hostRating != null && !table.hostIsGuest && (
            <span className="lobby-row-rating">{Math.round(table.hostRating)}</span>
          )}
        </div>
      </div>

      {/* Il tuo tavolo: qui sotto il nome, dove sull'altrui riga non c'e'
          niente. `isMine` E' l'attesa — appena qualcuno si siede il tavolo
          viene ritirato — quindi non serve una prop in piu' per dirlo.
          `role="status"` sta sul paragrafo e non sull'<li>: sopra toglierebbe
          alla riga la sua semantica di voce di lista. */}
      {isMine && (
        <p className="lobby-row-waiting" role="status">
          <span className="lobby-row-spinner" aria-hidden="true" />
          {t('lobby.waitingAtTable')}
        </p>
      )}

      <div className="lobby-row-meta">
        <span className="lobby-chip lobby-chip-variant">
          {variantPill(table.variant).flag}
          {variantShortLabel(t, table.variant)}
        </span>
        <span className="lobby-chip lobby-chip-cadence">
          <span className="lobby-chip-ico" aria-hidden="true">
            {CADENCE_ICONS[cadence?.category ?? 'blitz']}
          </span>
          {cadence ? cadence.clock : t('game.timeControls.noTimeLimit')}
        </span>
        <span className={`lobby-chip lobby-chip-rated${matchType === 'rated' ? ' is-rated' : ''}`}>
          {matchTypeLabel(t, table.matchType, table.rated)}
        </span>
        {/* Only what departs from the ordinary game gets a chip — a row full of
            "normal / free opening / 1 game" would say nothing. */}
        {table.gameMode !== DEFAULT_GAME_MODE && (
          <span className="lobby-chip">{gameModeLabel(t, table.gameMode)}</span>
        )}
        {drawn && (
          <span className="lobby-chip">{openingOptionLabel(t, table.opening, table.variant)}</span>
        )}
        {table.legs === 2 && (
          <span className="lobby-chip">{t('online.challenge.legsTwo')}</span>
        )}
        {table.hasPosition && <span className="lobby-chip">{t('lobby.customPosition')}</span>}
      </div>

      {isMine ? (
        <button type="button" className="lobby-row-action is-cancel" onClick={onCancel} disabled={busy}>
          {t('lobby.cancelTable')}
        </button>
      ) : barred ? (
        <span className="lobby-row-barred">{t('online.challenge.registeredOnly')}</span>
      ) : (
        <button type="button" className="lobby-row-action" onClick={() => onSit(table.id)} disabled={busy}>
          {t('lobby.sit')}
        </button>
      )}
    </li>
  );
}
